import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Proxy button for the sticky buy bar that clicks the main form's
 * add-to-cart button instead of submitting its own form.
 * This avoids Recharge detecting duplicate product forms.
 *
 * Button text and disabled state are handled by the nested
 * one-add-to-cart-component (see 1-add-to-cart-component.js).
 *
 * @extends Component
 */
class StickyAddToCartComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section');
    this.#abortController = new AbortController();
    this.#bindEvents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#section = null;
  }

  /**
   * Bind click on the proxy button and quantity sync events.
   */
  #bindEvents() {
    const signal = this.#abortController?.signal;
    if (!signal) return;

    const target = this.#section || document;
    target.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    target.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    // Bind click natively — cannot use on:click="/handleClick" because
    // the button is inside one-add-to-cart-component which would intercept
    // it via AddToCartComponent.handleClick (requires a <form>).
    const btn = this.querySelector('button[ref="addToCartButton"]');
    if (btn) {
      btn.addEventListener('click', this.#handleProxyClick, { signal });
    }
  }

  /**
   * Sync quantity from sticky bar to the main form, then click add-to-cart.
   */
  #handleProxyClick = () => {
    if (!this.#section) return;

    // Sync quantity to main form
    const stickyQty = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="quantity"]')
    );
    if (stickyQty) {
      const mainQty = /** @type {HTMLInputElement | null} */ (
        this.#section.querySelector(
          'one-recharge-product input[name="quantity"]'
        )
      );
      if (mainQty && mainQty.value !== stickyQty.value) {
        mainQty.value = stickyQty.value;
        mainQty.dispatchEvent(new Event('input', { bubbles: true }));
        mainQty.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    const mainButton = /** @type {HTMLButtonElement | null} */ (
      this.#section.querySelector('one-recharge-product button[ref="addToCartButton"]')
    );

    if (mainButton && !mainButton.disabled) {
      mainButton.click();
    }
  }

  /**
   * Handle variant update events.
   * Disable/enable the proxy button based on variant availability.
   * Text updates are handled by one-add-to-cart-component.
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    const { detail } = event;
    if (!detail?.resource) return;

    const addToCartComponent = /** @type {HTMLElement | null} */ (
      this.querySelector('one-add-to-cart-component')
    );
    if (!addToCartComponent) return;

    const btn = /** @type {HTMLButtonElement | null} */ (
      addToCartComponent.querySelector('button[ref="addToCartButton"]')
    );
    if (!btn) return;

    if (detail.resource.available === false || detail.resource == null) {
      btn.disabled = true;
    } else {
      btn.disabled = false;
    }
  };

  /**
   * Handle quantity selector update events.
   * Sync quantity between sticky bar and main form bidirectionally.
   * @param {CustomEvent} event
   */
  #handleQuantityUpdate = (event) => {
    const { detail } = event;
    if (!detail?.quantity || !this.#section) return;

    const target = /** @type {Element | null} */ (event.target);
    if (!target) return;

    const quantityValue = String(detail.quantity);
    const isFromStickyBar = this.contains(target);

    if (isFromStickyBar) {
      // Sync to main form
      const mainQty = /** @type {HTMLInputElement | null} */ (
        this.#section.querySelector(
          'one-recharge-product input[name="quantity"]'
        )
      );
      if (mainQty && mainQty.value !== quantityValue) {
        mainQty.value = quantityValue;
        mainQty.dispatchEvent(new Event('input', { bubbles: true }));
        mainQty.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      // Sync from main form to sticky bar
      const stickyQty = /** @type {HTMLInputElement | null} */ (
        this.querySelector('input[name="quantity"]')
      );
      if (stickyQty && stickyQty.value !== quantityValue) {
        stickyQty.value = quantityValue;
      }
    }
  };
}

if (!customElements.get('sticky-add-to-cart-component')) {
  customElements.define('sticky-add-to-cart-component', StickyAddToCartComponent);
}
